import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import { withStyles } from '@material-ui/core';

import Transition from '../../components/Dialogs/Transition';
import StationTankSelect from '../../components/pureForm/StationTankSelect';
import FMPDatePicker from '../../components/pureForm/FMPDatePicker';
import { add as addSalesOverride } from '../../modules/reports/salesOverride';

const styles = () => ({
	dialogPaper: {
		maxWidth: '100%',
		width: 520,
	},
	dialogPadding: {
		padding: '10px 25px 20px 25px',
	},
	patienceNote: {
		fontSize: '0.85rem',
		backgroundColor: '#fff8e1',
		border: '1px solid #ffe082',
		borderRadius: 4,
		padding: '10px 14px',
		marginBottom: 16,
	},
	errorText: {
		color: '#c62828',
		fontSize: '0.85rem',
		marginTop: 8,
	},
});

/**
 * Add a manual sales override for one station/tank/date on the Station Daily Sales
 * report. Volume is whole gallons; a reason is required and shows in the override
 * icon's popover on the report.
 */
class AddSalesOverrideDialog extends React.Component {
	constructor(props) {
		super(props);
		this.state = this.getFreshState();
	}

	getFreshState = () => {
		return {
			stationTank: {},
			salesDate: moment().subtract(1, 'day').format('YYYY-MM-DD'),
			volumeText: '',
			reason: '',
			isSaving: false,
			error: null,
		};
	};

	componentDidUpdate(prevProps) {
		//- Reset the form each time the dialog opens
		if (this.props.open && !prevProps.open) {
			this.setState(this.getFreshState());
		}
	}

	parseVolume = () => {
		const trimmed = _.trim(this.state.volumeText);
		if (!/^\d+$/.test(trimmed)) {
			return null;
		}
		return parseInt(trimmed, 10);
	};

	onChange = (value, path) => {
		this.setState({ [path]: value, error: null });
	};

	handleSave = () => {
		const { onSaved } = this.props;
		const { stationTank, salesDate, reason } = this.state;
		const volume = this.parseVolume();

		if (!stationTank || !_.isNumber(stationTank.stationId)) {
			return this.setState({ error: 'Pick a station and tank' });
		}
		if (!salesDate) {
			return this.setState({ error: 'Pick the sales date to override' });
		}
		if (volume == null) {
			return this.setState({ error: 'Volume must be a whole number of gallons (0 or more)' });
		}
		if (!_.trim(reason)) {
			return this.setState({ error: 'A reason for the override is required' });
		}

		this.setState({ isSaving: true, error: null });
		addSalesOverride({
			stationId: stationTank.stationId,
			tankNumber: stationTank.tankNumber,
			salesDate,
			volume,
			reason: _.trim(reason),
			callback: (err) => {
				if (err) {
					const errors = _.isArray(err) ? err : [err];
					return this.setState({
						isSaving: false,
						error: errors.map((e) => (e && e.message) || String(e)).join(', '),
					});
				}
				this.setState({ isSaving: false });
				onSaved();
			},
		});
	};

	render() {
		const { classes, open, onClose, stations } = this.props;
		const { stationTank, salesDate, volumeText, reason, isSaving, error } = this.state;

		const volume = this.parseVolume();
		const canSave =
			_.isNumber(stationTank.stationId) && !!salesDate && volume != null && !!_.trim(reason);

		return (
			<Dialog
				classes={{ paper: classes.dialogPaper }}
				open={open}
				TransitionComponent={Transition}
				onClose={isSaving ? undefined : onClose}
			>
				<div className={classes.dialogPadding}>
					<DialogTitle>Add Sales Override</DialogTitle>
					<DialogContent>
						<div className={classes.patienceNote}>
							The sales data is recomputed every 5 minutes, so please be patient. Allow up to 10
							minutes for this override to show on the report.
						</div>

						<StationTankSelect
							label="Station / Tank"
							path="stationTank"
							value={stationTank}
							onChange={this.onChange}
							stations={stations || []}
							md={12}
							doNotShowInactive
						/>

						<FMPDatePicker
							label="Sales Date"
							path="salesDate"
							value={salesDate}
							onChange={this.onChange}
							format="MM/DD/YYYY"
							md={12}
							contained
						/>

						<TextField
							label="Volume (gallons)"
							value={volumeText}
							fullWidth
							required
							helperText="Whole gallons sold that day (0 or more)."
							onChange={(e) => this.setState({ volumeText: e.target.value, error: null })}
						/>
						<TextField
							label="Reason"
							value={reason}
							fullWidth
							required
							multiline
							rows={3}
							style={{ marginTop: 12 }}
							helperText="Required. Shows in the override icon on the report."
							onChange={(e) => this.setState({ reason: e.target.value, error: null })}
						/>

						{error && <div className={classes.errorText}>{error}</div>}
					</DialogContent>
					<DialogActions>
						<Button onClick={onClose} disabled={isSaving}>
							Cancel
						</Button>
						<Button
							color="primary"
							variant="contained"
							onClick={this.handleSave}
							disabled={isSaving || !canSave}
						>
							{isSaving ? <CircularProgress size={20} /> : 'Add Override'}
						</Button>
					</DialogActions>
				</div>
			</Dialog>
		);
	}
}

AddSalesOverrideDialog.propTypes = {
	classes: PropTypes.object.isRequired,
	open: PropTypes.bool.isRequired,
	stations: PropTypes.array,
	onClose: PropTypes.func.isRequired,
	onSaved: PropTypes.func.isRequired,
};

export default withStyles(styles)(AddSalesOverrideDialog);
